import qs from "qs";

import { GetDataWithErrors } from "./Functions/SettingData";

// API module for Asset Management -> Assets tab.
//
// GetDataWithErrors rather than GetData, for the same reason NetworkEntityApi
// uses it: these endpoints return per-field errors in `data.errors` on a
// 400/409, and GetData keeps only responseStatus.code and .msg. It also refuses
// to re-issue a failed POST/PUT/DELETE, which may already have been applied.
//
// NOTHING HERE MOVES AN ASSET. currentStateCode, currentEntityID and
// receiptCount are a cache of assetLifecycleRecord and the backend rejects them
// on create and update -- state and custody change only through an approved
// work order, in WorkOrderApi.

// Search, sort and filter params shared by the list, the counts and the export,
// built in ONE place so a download cannot narrow differently from the table it
// was started from. The backend enforces the same pairing with one schema mixin
// and one query builder; this is the client half of that contract.
//
// Only defined params are sent: the schemas use unknown=RAISE, so an explicit
// `search=undefined` serialises to an empty string and is rejected rather than
// read as "no search".
// One multi-select filter, encoded for the transport that is about to carry it.
//
// The three filter fields take one OR many values under the SAME param name
// (CommaSeparatedStrList / CommaSeparatedIntList, capped at 200), so the
// mapping hands them down as arrays and the choice of encoding is made here,
// once:
//
//   - a query string gets `stateCode=inStock,deployed`. NOT a repeated key:
//     every list view loads its query string with request.args.to_dict(),
//     which flattens a MultiDict and keeps only the FIRST value, so
//     `stateCode=a&stateCode=b` would narrow to `a` while the panel showed two
//     chips. qs.stringify of an array produces exactly that shape, which is
//     why the join is not left to it.
//   - a POST body gets the real array, which is the natural JSON encoding and
//     what the same field accepts there.
//
// An empty selection sends NOTHING. An empty list would reach the query layer
// as IN (), which reads to a user as "this filter found no rows" rather than
// "no filter".
const encodeFilterValues = (value, asQueryString) => {
    const values = (Array.isArray(value) ? value : [value]).filter(
        (item) => item !== null && item !== undefined && item !== ""
    );
    if (!values.length) return undefined;
    return asQueryString ? values.join(",") : values;
};

export const buildAssetParams = (
    {
        assetTypeCode,
        stateCode,
        currentEntityID,
        search,
        searchBy,
        sortBy,
        sortOrder,
    },
    // The list and the counts serialise to a query string; list-action posts a
    // JSON body and passes false.
    { asQueryString = true } = {}
) => {
    const params = {};
    const assetTypeCodes = encodeFilterValues(assetTypeCode, asQueryString);
    if (assetTypeCodes !== undefined) params.assetTypeCode = assetTypeCodes;
    const stateCodes = encodeFilterValues(stateCode, asQueryString);
    if (stateCodes !== undefined) params.stateCode = stateCodes;
    const currentEntityIDs = encodeFilterValues(currentEntityID, asQueryString);
    if (currentEntityIDs !== undefined) params.currentEntityID = currentEntityIDs;
    if (search) params.search = search;
    if (searchBy) params.searchBy = searchBy;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    return params;
};

// GET /api/assets/config -> { assetStates: [...] }
// Labels and badge themes for the state column and the state filter. Guarded by
// ASSETS:view alone, so a user who can see this tab can always render it.
export const getAssetConfig = async () =>
    GetDataWithErrors({ method: "get", url: "/api/assets/config" });

// GET /api/assets
export const getAssets = async ({ page = 1, limit = 25, ...rest }) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/assets?${qs.stringify({
            ...buildAssetParams(rest),
            page,
            limit,
        })}`,
    });

// GET /api/assets/<id>
export const getAsset = async (assetID) =>
    GetDataWithErrors({ method: "get", url: `/api/assets/${assetID}` });

// GET /api/assets/<id>/lifecycle -- the timeline, newest first.
// Reads assetLifecycleRecord, never the assetMeta cache beside it.
export const getAssetLifecycle = async (assetID, { page = 1, limit = 25 } = {}) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/assets/${assetID}/lifecycle?${qs.stringify({ page, limit })}`,
    });

// GET /api/assets/search-suggestions
//
// Reshaped to { responseStatus, response: { data } } because DeviceSearchInput
// reads res.response.data -- the shape GetData produces. Returning the envelope
// untouched yields an always-empty dropdown with no error anywhere.
export const getAssetSearchSuggestions = async (columnName, columnSearch, columnLimit = 10) => {
    const res = await GetDataWithErrors({
        method: "get",
        url: `/api/assets/search-suggestions?${qs.stringify({
            columnName,
            columnSearch,
            columnLimit,
        })}`,
    });
    const values = Array.isArray(res?.data) ? res.data : [];
    return {
        responseStatus: res?.responseStatus || { code: 0, msg: null },
        response: { data: values },
    };
};

// POST /api/assets/list-action -- one endpoint, two actions.
//
// The house pattern; there is no separate /export or /email.
//
// UNLIKE the Network tab, this sends NO `rows`. The server builds the file
// itself, from the same query and the same column selection, and `actionType`
// decides only whether it is also attached to an email -- so the download and
// the mail are literally the same rows and cannot disagree. The schema is
// unknown=RAISE, so sending `rows` is a 400, not an ignored field.
//
// SELECTION IS THE INVENTORY CONTRACT: `resourceIDs` (asset ids) and
// `selectAll`, on BOTH actions. The server ignores resourceIDs when selectAll
// is true and narrows to them otherwise. Nothing selected sends neither field,
// which the server also reads as the whole filtered set -- and which an older
// backend without these fields still accepts.
const selectionBody = (resourceIDs, selectAll) => {
    const ids = Array.isArray(resourceIDs) ? resourceIDs : [];
    if (selectAll) return { resourceIDs: [], selectAll: true };
    if (ids.length) return { resourceIDs: ids, selectAll: false };
    return {};
};

export const assetListAction = async ({
    actionType,
    columns,
    columnOrder,
    sendTo,
    sendCc,
    subject,
    body,
    fileName,
    resourceIDs,
    selectAll,
    ...filters
}) =>
    GetDataWithErrors({
        method: "post",
        url: "/api/assets/list-action",
        headers: { "Content-Type": "application/json" },
        data: {
            actionType,
            columns,
            columnOrder,
            // Only sent when there is a selection: with nothing ticked the
            // request is exactly what an older backend (no resourceIDs /
            // selectAll in its schema, unknown fields rejected) accepts, so an
            // unticked download or email keeps working whatever the deploy
            // order. Absent fields mean the whole filtered set either way.
            ...selectionBody(resourceIDs, selectAll),
            ...(actionType === "mail"
                ? { sendTo, sendCc: sendCc || [], subject, body, fileName }
                : {}),
            ...buildAssetParams(filters, { asQueryString: false }),
        },
    });

// POST /api/assets HAS NO CLIENT, DELIBERATELY.
//
// It used to register a serial that arrived outside a work order, and that
// wrote no lifecycle row -- so the asset started with no state, no custody and
// no movement behind it, which the timeline has nowhere to show and nobody can
// explain afterwards. An asset now enters the system through a Vendor Intake
// work order and only through one.
//
// The endpoint still exists server-side for imports and for the work order
// service itself. If a screen ever needs it again, add the client back with
// the reason it is allowed to write a stateless asset.

// PUT /api/assets/<id>
// assetTypeCode is immutable -- it is half the soft link to the table that
// actually describes the thing, and re-typing would point the reference into
// the wrong one. serialNumber IS editable, for typo correction.
export const updateAsset = async (assetID, payload) =>
    GetDataWithErrors({
        method: "put",
        url: `/api/assets/${assetID}`,
        headers: { "Content-Type": "application/json" },
        data: payload,
    });

// DELETE /api/assets/<id> -- soft delete.
//
// deleteReason is required and may not be blank: the serial is never recycled,
// so this is the only thing that will ever explain the disappearance.
export const deleteAsset = async (assetID, deleteReason) =>
    GetDataWithErrors({
        method: "delete",
        url: `/api/assets/${assetID}`,
        headers: { "Content-Type": "application/json" },
        data: { deleteReason },
    });
